import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
  CalendarDaysIcon,
  CalendarIcon,
  UserCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { ArrowRightIcon } from "@heroicons/react/20/solid";
import { usePortalAuth } from "../context/PortalAuthContext";

export default function PortalClientPanel() {
  const { user, isAuthenticated } = usePortalAuth();

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  if (user?.role === "psychologist") return <Navigate to="/psicologo" replace />;
  if (user?.role !== "client") return <Navigate to="/login" replace />;

  return (
    <>
    <div className="max-w-3xl mx-auto space-y-8">

      {/* ── Greeting ── */}
      <div className="flex items-center gap-5">
        <Link to="/mi-perfil" className="shrink-0 group" title="Editar mi perfil">
          {user.profileImageUrl ? (
            <img
              src={user.profileImageUrl}
              alt=""
              className="h-16 w-16 rounded-full object-cover border-2 border-white shadow ring-2 ring-brand-lavender/20 group-hover:ring-brand-navy/30 transition-all"
            />
          ) : (
            <div className="h-16 w-16 rounded-full bg-brand-beige border border-brand-lavender/20 flex items-center justify-center text-brand-gray shadow-sm group-hover:border-brand-navy/30 transition-all">
              <UserCircleIcon className="h-10 w-10" />
            </div>
          )}
        </Link>
        <div className="min-w-0">
          <h1 className="font-sans text-2xl sm:text-3xl font-semibold tracking-tight text-brand-dark">
            Hola, {user.firstName || "bienvenido"}
          </h1>
          <p className="mt-0.5 text-sm text-brand-gray">
            ¿Qué necesitas hacer hoy?
          </p>
        </div>
      </div>

      {/* ── Action cards ── */}
      <div className="grid sm:grid-cols-3 gap-4">

        {/* Primary — Agendar (brand-navy) */}
        <Link
          to="/agendar"
          className="group relative flex flex-col justify-between rounded-2xl bg-brand-navy p-6 shadow-lg shadow-brand-navy/15 hover:brightness-110 transition-all sm:col-span-1"
        >
          <span className="inline-flex p-2.5 rounded-xl bg-white/10 text-white w-fit">
            <CalendarDaysIcon className="h-6 w-6" aria-hidden />
          </span>
          <div className="mt-10">
            <p className="text-white font-semibold text-lg leading-tight">
              Agendar cita
            </p>
            <p className="mt-1 text-white/60 text-sm leading-snug">
              Elige psicólogo, día y horario disponible.
            </p>
          </div>
          <span className="mt-5 inline-flex items-center gap-1 text-white/80 text-sm font-medium group-hover:text-white transition-colors">
            Continuar <ArrowRightIcon className="h-4 w-4" aria-hidden />
          </span>
        </Link>

        {/* Mis citas */}
        <Link
          to="/mis-citas"
          className="group flex flex-col justify-between rounded-2xl bg-white border border-brand-lavender/20 p-6 shadow-sm hover:shadow-md hover:border-brand-navy/20 transition-all"
        >
          <span className="inline-flex p-2.5 rounded-xl bg-brand-beige text-brand-navy w-fit">
            <CalendarIcon className="h-6 w-6" aria-hidden />
          </span>
          <div className="mt-10">
            <p className="text-brand-dark font-semibold text-lg leading-tight">
              Mis citas
            </p>
            <p className="mt-1 text-brand-gray text-sm leading-snug">
              Consulta tus sesiones y cancela si es necesario.
            </p>
          </div>
          <span className="mt-5 inline-flex items-center gap-1 text-brand-navy text-sm font-medium group-hover:underline">
            Ver citas <ArrowRightIcon className="h-4 w-4" aria-hidden />
          </span>
        </Link>

        {/* Mi perfil */}
        <Link
          to="/mi-perfil"
          className="group flex flex-col justify-between rounded-2xl bg-white border border-brand-lavender/20 p-6 shadow-sm hover:shadow-md hover:border-brand-navy/20 transition-all"
        >
          <span className="inline-flex p-2.5 rounded-xl bg-brand-beige text-brand-navy w-fit">
            <UserCircleIcon className="h-6 w-6" aria-hidden />
          </span>
          <div className="mt-10">
            <p className="text-brand-dark font-semibold text-lg leading-tight">
              Mi perfil
            </p>
            <p className="mt-1 text-brand-gray text-sm leading-snug">
              Actualiza tus datos y contraseña.
            </p>
          </div>
          <span className="mt-5 inline-flex items-center gap-1 text-brand-navy text-sm font-medium group-hover:underline">
            Editar <ArrowRightIcon className="h-4 w-4" aria-hidden />
          </span>
        </Link>
      </div>

      {/* ── IMPORTANTE banner ── */}
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3">
        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" aria-hidden />
        <p className="text-sm text-red-700 leading-relaxed">
          <span className="font-semibold">IMPORTANTE: </span>
          En caso de que requieras asistencia inmediata, comunícate a los teléfonos{" "}
          <a href="tel:8006819591" className="font-bold underline">800 681 9591</a>
          {" "}y{" "}
          <a href="tel:3341708343" className="font-bold underline">33 4170 8343</a>
          , disponibles las 24 horas, los 365 días del año.
          Atención profesional · especializada · confidencial · gratuita.
        </p>
      </div>

      {/* ── Support ── */}
      <div className="rounded-2xl border border-brand-lavender/20 bg-white p-6 flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-brand-dark mb-3">
            ¿Necesitas ayuda para agendar?
          </p>
          <div className="flex flex-col gap-2">
            <a
              href="tel:8006819591"
              className="inline-flex items-center gap-2 text-sm text-brand-gray hover:text-brand-navy transition-colors"
            >
              <PhoneIcon className="h-4 w-4 shrink-0 text-brand-lavender" aria-hidden />
              <span>
                <span className="font-medium text-brand-dark">800 681 9591</span>
                {" / "}
                <span className="font-medium text-brand-dark">334 170 8343</span>
              </span>
            </a>
            <a
              href="mailto:contacto@eapconsulting.mx"
              className="inline-flex items-center gap-2 text-sm text-brand-gray hover:text-brand-navy transition-colors"
            >
              <EnvelopeIcon className="h-4 w-4 shrink-0 text-brand-lavender" aria-hidden />
              <span className="font-medium text-brand-dark">contacto@eapconsulting.mx</span>
            </a>
          </div>
        </div>
        <p className="text-xs text-brand-gray sm:max-w-[180px] leading-relaxed">
          Nuestro equipo te ayudará con lo que necesites.
        </p>
      </div>

    </div>

      {/* ── Hero ── */}
      <div className="-mx-3 sm:-mx-4 mb-0 mt-8">
        <div
          className="min-h-[12rem] sm:min-h-[16rem] bg-cover bg-center"
          style={{ backgroundImage: "url('/images/agendar-hero.jpg')" }}
          role="img"
          aria-label="Portal EAP Consulting"
        />
      </div>
    </>
  );
}
